import React, { useMemo, useState } from "react";
import {
  ChakraProvider,
  ColorModeScript,
  extendTheme,
  useColorMode,
  Box,
  Grid,
  GridItem,
  Flex,
  Heading,
  Text,
  Table,
  Thead,
  Tbody,
  Tfoot,
  Tr,
  Th,
  Td,
  Input,
  IconButton,
  Button,
  HStack,
  VStack,
  Spacer,
  Divider,
  useToast,
  NumberInput,
  NumberInputField,
  Switch,
  Badge,
  Checkbox,
} from "@chakra-ui/react";
import {
  AddIcon,
  DeleteIcon,
  EditIcon,
  CheckIcon,
  CloseIcon,
  MoonIcon,
  SunIcon,
} from "@chakra-ui/icons";

// ---- THEME
const theme = extendTheme({
  initialColorMode: "light",
  useSystemColorMode: false,
  fonts: {
    heading:
      "Inter, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif",
    body: "Inter, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif",
  },
  styles: {
    global: {
      body: { fontSize: "13px", bg: "gray.50", _dark: { bg: "gray.800" } },
      th: {
        fontWeight: 700,
        fontSize: "12px",
        letterSpacing: "0.02em",
        textTransform: "none",
        color: "gray.600",
        _dark: { color: "gray.300" },
      },
      td: {
        fontSize: "13px",
        py: "2",
      },
    },
  },
  components: {
    Table: {
      variants: {
        simple: {
          th: {
            borderBottom: "2px",
            borderColor: "gray.200",
            _dark: { borderColor: "gray.600" },
          },
        },
      },
    },
    NumberInput: {
      defaultProps: {
        focusBorderColor: "teal.400",
      },
    },
  },
});

// ---- GENERIC INLINE-EDIT CELL
const EditableCell = ({ value, onChange, isNumeric }) => {
  return (
    <NumberInput
      size="sm"
      value={value ?? ""}
      onChange={(v) => onChange(v)}
      min={0}
      precision={2}
      clampValueOnBlur
      keepWithinRange={!isNumeric}
    >
      <NumberInputField px={1} />
    </NumberInput>
  );
};

// ---- GENERIC CRUD TABLE
const CrudTable = ({ title, columns, rows, setRows, totalsRow, compact }) => {
  const toast = useToast();
  const addRow = () =>
    setRows((r) => [
      ...r,
      Object.fromEntries(columns.map((c) => [c.accessor, c.default ?? ""])),
    ]);
  const deleteRow = (idx) => setRows((r) => r.filter((_, i) => i !== idx));
  const updateCell = (idx, key, val) =>
    setRows((r) =>
      r.map((row, i) => (i === idx ? { ...row, [key]: val } : row))
    );

  return (
    <Box
      borderWidth="1px"
      rounded="2xl"
      p={4}
      overflow="hidden"
      bg="white"
      _dark={{ bg: "gray.800" }}
      shadow="sm"
      transition="all 0.2s"
      _hover={{ shadow: "md" }}
    >
      <Flex align="center" mb={2} gap={2}>
        <Heading size="sm">{title}</Heading>
        <Spacer />
        <IconButton
          aria-label="add"
          size="sm"
          icon={<AddIcon />}
          onClick={addRow}
        />
      </Flex>
      <Box overflowX="auto">
        <Table size={compact ? "sm" : "md"} variant="simple">
          <Thead>
            <Tr>
              {columns.map((c) => (
                <Th
                  key={c.accessor}
                  textAlign={c.isNumeric ? "right" : "left"}
                  minW={c.minW || 24}
                >
                  {c.header}
                </Th>
              ))}
              <Th isNumeric minW={16}>
                Actions
              </Th>
            </Tr>
          </Thead>
          <Tbody>
            {rows.map((row, idx) => (
              <Tr
                key={idx}
                _hover={{
                  bg: "blackAlpha.50",
                  _dark: { bg: "whiteAlpha.100" },
                }}
              >
                {columns.map((c) => (
                  <Td key={c.accessor} isNumeric={c.isNumeric}>
                    {c.type === "text" ? (
                      <Input
                        size="sm"
                        value={row[c.accessor] ?? ""}
                        onChange={(e) =>
                          updateCell(idx, c.accessor, e.target.value)
                        }
                      />
                    ) : (
                      <EditableCell
                        value={row[c.accessor]}
                        onChange={(v) => updateCell(idx, c.accessor, v)}
                        isNumeric={c.isNumeric}
                      />
                    )}
                  </Td>
                ))}
                <Td isNumeric>
                  <IconButton
                    aria-label="delete"
                    size="xs"
                    variant="ghost"
                    icon={<DeleteIcon />}
                    onClick={() => deleteRow(idx)}
                  />
                </Td>
              </Tr>
            ))}
            {totalsRow && (
              <Tr
                fontWeight="bold"
                bg="blackAlpha.50"
                _dark={{ bg: "whiteAlpha.100" }}
              >
                {columns.map((c, i) => (
                  <Td key={c.accessor} isNumeric={c.isNumeric}>
                    {i === 0 ? "Total" : totalsRow(c.accessor, rows)}
                  </Td>
                ))}
                <Td></Td>
              </Tr>
            )}
          </Tbody>
        </Table>
      </Box>
    </Box>
  );
};

// ---- DENOMINATIONS CARD
const DenomsCard = ({ denoms, setDenoms }) => {
  const rows = Object.entries(denoms);
  const total = rows.reduce(
    (acc, [note, obj]) => acc + Number(note) * (Number(obj.qty) || 0),
    0
  );
  const setQty = (note, qty) => setDenoms((d) => ({ ...d, [note]: { qty } }));
  return (
    <Box
      borderWidth="1px"
      rounded="2xl"
      p={4}
      bg="white"
      _dark={{ bg: "gray.800" }}
      shadow="sm"
      transition="all 0.2s"
      _hover={{ shadow: "md" }}
    >
      <Heading size="sm" mb={4} color="teal.600" _dark={{ color: "teal.200" }}>
        Denominations
      </Heading>
      <Table size="sm">
        <Thead>
          <Tr>
            <Th isNumeric>Note</Th>
            <Th isNumeric>Qty</Th>
            <Th isNumeric>Amount</Th>
          </Tr>
        </Thead>
        <Tbody>
          {rows.map(([note, obj]) => (
            <Tr key={note}>
              <Td isNumeric>{note}</Td>
              <Td isNumeric>
                <NumberInput
                  size="sm"
                  value={obj.qty ?? ""}
                  min={0}
                  onChange={(v) => setQty(note, v)}
                >
                  <NumberInputField px={1} />
                </NumberInput>
              </Td>
              <Td isNumeric>
                {(Number(note) * (Number(obj.qty) || 0)).toFixed(2)}
              </Td>
            </Tr>
          ))}
          <Tr
            fontWeight="bold"
            bg="blackAlpha.50"
            _dark={{ bg: "whiteAlpha.100" }}
          >
            <Td>Total</Td>
            <Td></Td>
            <Td isNumeric>{total.toFixed(2)}</Td>
          </Tr>
        </Tbody>
      </Table>
    </Box>
  );
};

// ---- HEADER STRIP
const Strip = ({ label, color }) => (
  <Flex
    align="center"
    justify="center"
    bg={color}
    color="white"
    h={10}
    rounded="xl"
    fontWeight="bold"
    letterSpacing="0.05em"
    fontSize="md"
    shadow="sm"
    transition="all 0.2s"
    _hover={{
      transform: "translateY(-1px)",
      shadow: "md",
    }}
  >
    {label}
  </Flex>
);

// ---- MAIN SCREEN
function CashierSurface() {
  const { colorMode, toggleColorMode } = useColorMode();
  const toast = useToast();

  // POS matrix: 300 rows with amount and verification status
  const [posRows, setPosRows] = useState(
    Array.from({ length: 300 }, (_, i) => ({
      sr: i + 1,
      a: "",
      verified: false,
    }))
  );

  const sumCol = (key) =>
    posRows.reduce((acc, r) => acc + (Number(r[key]) || 0), 0);

  const updatePos = (idx, key, val) =>
    setPosRows((rows) =>
      rows.map((r, i) => (i === idx ? { ...r, [key]: val } : r))
    );

  // Kotak QR / Swipe / Debtors / Returns generic lists
  const [kotakQR, setKotakQR] = useState([]);
  const [kotakSwipe, setKotakSwipe] = useState([]);
  const [debtors, setDebtors] = useState([]); // {sr, salesman, party, amount}
  const [returnsList, setReturnsList] = useState([]); // {sr, salesman, amount}
  const [debitCash, setDebitCash] = useState([]); // right-most simple list {ref, amount}

  const genericColsSmall = [
    { header: "Sr. No.", accessor: "sr", type: "text", default: "" },
    { header: "Salesman", accessor: "salesman", type: "text", default: "" },
    { header: "Amount", accessor: "amount", isNumeric: true },
  ];
  const swipeCols = [
    { header: "Sr. No.", accessor: "sr", type: "text", default: "" },
    { header: "Salesman", accessor: "salesman", type: "text", default: "" },
    { header: "Amount", accessor: "amount", isNumeric: true },
  ];
  const debtCols = [
    { header: "Sr. No.", accessor: "sr", type: "text" },
    { header: "Salesman", accessor: "salesman", type: "text" },
    { header: "Party", accessor: "party", type: "text" },
    { header: "Amount", accessor: "amount", isNumeric: true },
  ];

  const listTotal = (key, rows) =>
    rows.reduce((a, r) => a + (Number(r[key]) || 0), 0).toFixed(2);

  // Denoms
  const [denoms, setDenoms] = useState({
    2000: { qty: "" },
    500: { qty: "" },
    200: { qty: "" },
    100: { qty: "" },
    50: { qty: "" },
    20: { qty: "" },
    10: { qty: "" },
    5: { qty: "" },
    2: { qty: "" },
    1: { qty: "" },
  });
  const denomTotal = useMemo(
    () =>
      Object.entries(denoms).reduce(
        (acc, [note, obj]) => acc + Number(note) * (Number(obj.qty) || 0),
        0
      ),
    [denoms]
  );

  // Cash summary
  const [openingCash, setOpeningCash] = useState(0);
  const swipeTotal = useMemo(
    () =>
      listTotal(
        "amount",
        kotakSwipe.map((x) => x)
      ),
    [kotakSwipe]
  );
  const qrTotal = useMemo(
    () =>
      listTotal(
        "amount",
        kotakQR.map((x) => x)
      ),
    [kotakQR]
  );
  const debtorsTotal = useMemo(
    () =>
      listTotal(
        "amount",
        debtors.map((x) => x)
      ),
    [debtors]
  );
  const returnsTotal = useMemo(
    () =>
      listTotal(
        "amount",
        returnsList.map((x) => x)
      ),
    [returnsList]
  );
  const debitCashTotal = useMemo(
    () =>
      listTotal(
        "amount",
        debitCash.map((x) => x)
      ),
    [debitCash]
  );

  const numberVal = (x) => Number(String(x || 0));
  const totalSale = useMemo(
    () =>
      numberVal(sumCol("a")) +
      numberVal(qrTotal) +
      numberVal(swipeTotal) +
      numberVal(debtorsTotal),
    [posRows, qrTotal, swipeTotal, debtorsTotal]
  );
  const netAmount = useMemo(
    () =>
      numberVal(totalSale) -
      numberVal(debitCashTotal) -
      numberVal(returnsTotal),
    [totalSale, debitCashTotal, returnsTotal]
  );

  return (
    <Box
      p={6}
      ml="10px"
      maxW="calc(100vw - 150px)"
      minH="100vh"
      bg="white"
      _dark={{ bg: "gray.900" }}
      position="relative"
      overflowX="auto"
      sx={{
        "&::-webkit-scrollbar": {
          width: "8px",
          height: "8px",
          borderRadius: "8px",
          backgroundColor: `rgba(0, 0, 0, 0.05)`,
        },
        "&::-webkit-scrollbar-thumb": {
          backgroundColor: `rgba(0, 0, 0, 0.15)`,
          borderRadius: "8px",
        },
      }}
    >
      <VStack spacing={6} width="100%" align="stretch">
        {/* POS Grid */}
        <Box
          borderWidth="1px"
          rounded="2xl"
          p={4}
          bg="white"
          _dark={{ bg: "gray.800" }}
          shadow="sm"
          transition="all 0.2s"
          _hover={{ shadow: "md" }}
          mb={6}
          w="100%"
        >
          <Flex direction="column" gap={4}>
            <Text
              textAlign="center"
              fontWeight="bold"
              fontSize="lg"
              color="orange.600"
              _dark={{ color: "orange.200" }}
            >
              !! Shree Ganeshay Namha !!
            </Text>
            <Box overflowX="auto" maxH="600px">
              <Table
                size="sm"
                variant="simple"
                bg="white"
                _dark={{ bg: "gray.800" }}
              >
                <Thead
                  position="sticky"
                  top={0}
                  bg="white"
                  _dark={{ bg: "gray.800" }}
                  zIndex={1}
                  boxShadow="sm"
                >
                  <Tr>
                    <Th width="80px">Verify</Th>
                    <Th width="100px">SR. NO.</Th>
                    <Th>Amount</Th>
                    <Th width="80px">Verify</Th>
                    <Th width="100px">SR. NO.</Th>
                    <Th>Amount</Th>
                    <Th width="80px">Verify</Th>
                    <Th width="100px">SR. NO.</Th>
                    <Th>Amount</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {Array.from({ length: 100 }, (_, rowIdx) => (
                    <Tr
                      key={rowIdx}
                      _hover={{ bg: "gray.50" }}
                      _dark={{ _hover: { bg: "gray.700" } }}
                    >
                      {[0, 1, 2].map((colIdx) => {
                        const idx = rowIdx + colIdx * 100;
                        if (idx >= 300) return null;
                        return (
                          <React.Fragment key={colIdx}>
                            <Td>
                              <Checkbox
                                colorScheme="green"
                                size="sm"
                                isChecked={posRows[idx]?.verified || false}
                                isDisabled={
                                  idx > 0 &&
                                  (!posRows[idx - 1]?.verified ||
                                    !posRows[idx - 1]?.a)
                                }
                                onChange={(e) => {
                                  // Only allow verification if amount is entered
                                  if (!posRows[idx]?.a) {
                                    toast({
                                      title: "Cannot verify empty entry",
                                      description:
                                        "Please enter an amount first",
                                      status: "warning",
                                      duration: 2000,
                                      isClosable: true,
                                    });
                                    return;
                                  }

                                  const newRows = [...posRows];
                                  if (!newRows[idx]) newRows[idx] = {};
                                  newRows[idx] = {
                                    ...newRows[idx],
                                    verified: e.target.checked,
                                  };
                                  setPosRows(newRows);
                                }}
                              />
                            </Td>
                            <Td
                              isNumeric
                              fontWeight="medium"
                              color={
                                posRows[idx]?.verified
                                  ? "green.500"
                                  : "gray.600"
                              }
                              _dark={{
                                color: posRows[idx]?.verified
                                  ? "green.300"
                                  : "gray.300",
                              }}
                            >
                              {idx + 1}
                            </Td>
                            <Td>
                              <NumberInput
                                size="sm"
                                value={posRows[idx]?.a ?? ""}
                                onChange={(v) => {
                                  if (idx > 0 && !posRows[idx - 1]?.a) {
                                    toast({
                                      title: "Invalid Entry",
                                      description:
                                        "Please fill entries in sequence",
                                      status: "warning",
                                      duration: 2000,
                                      isClosable: true,
                                    });
                                    return;
                                  }
                                  updatePos(idx, "a", v);
                                }}
                                min={0}
                                precision={2}
                                isDisabled={idx > 0 && !posRows[idx - 1]?.a}
                              >
                                <NumberInputField
                                  textAlign="right"
                                  border="1px solid"
                                  borderColor={
                                    posRows[idx]?.verified
                                      ? "green.200"
                                      : "gray.200"
                                  }
                                  bg={
                                    posRows[idx]?.verified
                                      ? "green.50"
                                      : "white"
                                  }
                                  _dark={{
                                    bg: posRows[idx]?.verified
                                      ? "green.900"
                                      : "gray.800",
                                    borderColor: posRows[idx]?.verified
                                      ? "green.600"
                                      : "gray.600",
                                  }}
                                  _hover={{ borderColor: "blue.300" }}
                                  _focus={{
                                    borderColor: "blue.500",
                                    boxShadow:
                                      "0 0 0 1px var(--chakra-colors-blue-500)",
                                  }}
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter") {
                                      const nextIdx = idx + 1;
                                      if (nextIdx < 300) {
                                        const nextInput =
                                          document.querySelector(
                                            `input[name="pos-${nextIdx}"]`
                                          );
                                        if (nextInput) nextInput.focus();
                                      }
                                    }
                                  }}
                                  name={`pos-${idx}`}
                                />
                              </NumberInput>
                            </Td>
                          </React.Fragment>
                        );
                      })}
                    </Tr>
                  ))}
                </Tbody>
                <Tfoot
                  position="sticky"
                  bottom={0}
                  bg="white"
                  _dark={{ bg: "gray.800" }}
                  zIndex={1}
                  boxShadow="sm"
                >
                  <Tr bg="gray.50" _dark={{ bg: "gray.700" }}>
                    <Td colSpan={9}>
                      <Flex justify="space-between" align="center" p={2}>
                        <Box>
                          <Text fontWeight="bold">Total Amount:</Text>
                          <Text fontSize="sm" color="green.500">
                            {posRows.filter((row) => row?.verified).length} of
                            300 entries verified
                          </Text>
                        </Box>
                        <Text fontWeight="bold" fontSize="lg" color="teal.500">
                          {sumCol("a").toFixed(2)}
                        </Text>
                      </Flex>
                    </Td>
                  </Tr>
                </Tfoot>
              </Table>
            </Box>
          </Flex>
        </Box>

        {/* Payment Methods Grid */}
        <Grid
          templateColumns={{ base: "1fr", md: "repeat(3, 1fr)" }}
          gap={4}
          mb={6}
        >
          <CrudTable
            title="KOTAK QR"
            columns={genericColsSmall}
            rows={kotakQR}
            setRows={setKotakQR}
            compact
            totalsRow={(acc, rows) =>
              acc === "amount" ? listTotal("amount", rows) : ""
            }
          />
          <CrudTable
            title="KOTAK SWIPE"
            columns={swipeCols}
            rows={kotakSwipe}
            setRows={setKotakSwipe}
            compact
            totalsRow={(acc, rows) =>
              acc === "amount" ? listTotal("amount", rows) : ""
            }
          />
          <CrudTable
            title="DEBTORS"
            columns={debtCols}
            rows={debtors}
            setRows={setDebtors}
            compact
            totalsRow={(acc, rows) =>
              acc === "amount" ? listTotal("amount", rows) : ""
            }
          />
        </Grid>

        {/* Cash + Right rail */}
        <Grid templateColumns={{ base: "1fr", xl: "3fr 1fr" }} gap={3}>
          <GridItem>
            {/* Cash Panel */}
            <Box borderWidth="1px" rounded="2xl" p={3}>
              <Heading size="sm" mb={2}>
                Cash
              </Heading>

              <Grid
                templateColumns={{ base: "1fr", md: "1fr 1fr 1fr" }}
                gap={3}
              >
                <Box borderWidth="1px" rounded="lg" p={2}>
                  <Table size="sm">
                    <Tbody>
                      <Tr>
                        <Td>Opening Cash</Td>
                        <Td isNumeric>
                          <NumberInput
                            size="sm"
                            value={openingCash}
                            onChange={(v) => setOpeningCash(Number(v) || 0)}
                            min={0}
                            precision={2}
                          >
                            <NumberInputField px={1} />
                          </NumberInput>
                        </Td>
                      </Tr>
                      <Tr>
                        <Td fontWeight="bold">Total Sale</Td>
                        <Td isNumeric fontWeight="bold">
                          {Number(totalSale).toFixed(2)}
                        </Td>
                      </Tr>
                      <Tr>
                        <Td>Debit Cash</Td>
                        <Td isNumeric>{Number(debitCashTotal).toFixed(2)}</Td>
                      </Tr>
                      <Tr>
                        <Td>Returns</Td>
                        <Td isNumeric>{Number(returnsTotal).toFixed(2)}</Td>
                      </Tr>
                      <Tr>
                        <Td>Net Amount</Td>
                        <Td isNumeric>{Number(netAmount).toFixed(2)}</Td>
                      </Tr>
                    </Tbody>
                  </Table>
                  <Divider my={2} />
                  <Table size="sm">
                    <Tbody>
                      <Tr>
                        <Td>SWIPE</Td>
                        <Td isNumeric>{Number(swipeTotal).toFixed(2)}</Td>
                      </Tr>
                      <Tr>
                        <Td>DEBTORS</Td>
                        <Td isNumeric>{Number(debtorsTotal).toFixed(2)}</Td>
                      </Tr>
                      <Tr>
                        <Td>BHARATPE (QR)</Td>
                        <Td isNumeric>{Number(qrTotal).toFixed(2)}</Td>
                      </Tr>
                    </Tbody>
                  </Table>
                </Box>

                <DenomsCard denoms={denoms} setDenoms={setDenoms} />

                <Box borderWidth="1px" rounded="lg" p={2}>
                  <Heading size="sm" mb={2}>
                    Difference
                  </Heading>
                  <Table size="sm">
                    <Tbody>
                      <Tr>
                        <Td>Cash In Hand</Td>
                        <Td isNumeric>
                          {(openingCash + denomTotal).toFixed(2)}
                        </Td>
                      </Tr>
                      <Tr fontWeight="bold">
                        <Td>Expected</Td>
                        <Td isNumeric>{Number(netAmount).toFixed(2)}</Td>
                      </Tr>
                      <Tr>
                        <Td colSpan={2}>
                          <Divider />
                        </Td>
                      </Tr>
                      <Tr>
                        <Td>Difference</Td>
                        <Td isNumeric>
                          {(openingCash + denomTotal - netAmount).toFixed(2)}
                        </Td>
                      </Tr>
                    </Tbody>
                  </Table>
                </Box>
              </Grid>

              <Grid
                templateColumns={{ base: "1fr", md: "1fr 1fr" }}
                gap={3}
                mt={3}
              >
                <Box>
                  <CrudTable
                    title="Return"
                    columns={[
                      { header: "Sr. No.", accessor: "sr", type: "text" },
                      {
                        header: "Salesman",
                        accessor: "salesman",
                        type: "text",
                      },
                      { header: "Amount", accessor: "amount", isNumeric: true },
                    ]}
                    rows={returnsList}
                    setRows={setReturnsList}
                    compact
                    totalsRow={(acc, rows) =>
                      acc === "amount" ? listTotal("amount", rows) : ""
                    }
                  />
                </Box>

                <Box>
                  <CrudTable
                    title="Debit Cash"
                    columns={[
                      { header: "Ref.", accessor: "ref", type: "text" },
                      { header: "Amount", accessor: "amount", isNumeric: true },
                    ]}
                    rows={debitCash}
                    setRows={setDebitCash}
                    compact
                    totalsRow={(acc, rows) =>
                      acc === "amount" ? listTotal("amount", rows) : ""
                    }
                  />
                </Box>
              </Grid>
            </Box>
          </GridItem>

          {/* Right Clean Column as spacer */}
          {/* <GridItem>
            <Box h="full" borderWidth="1px" rounded="2xl" p={3}>
              <VStack align="stretch" spacing={2}>
                <Heading size="sm">Notes</Heading>
                <Text fontSize="xs" opacity={0.7}>
                  This panel intentionally left minimal to emulate the narrow
                  side bands in the reference sheet. Use the CRUD lists to the
                  left.
                </Text>
              </VStack>
            </Box>
          </GridItem> */}
        </Grid>
      </VStack>
    </Box>
  );
}

export default function CashierScreen() {
  return (
    <ChakraProvider theme={theme}>
      <ColorModeScript initialColorMode={theme.initialColorMode} />
      <CashierSurface />
    </ChakraProvider>
  );
}
